import { FC, useEffect } from 'react';
import { IonContent, IonModal } from '@ionic/react';
import { useSubscription } from '@autopylot-internal/state';
import { createUseStyles } from 'react-jss';
import { useInstance } from '../support/ioc/useInstance';
import { PageHeader } from '../components/page/PageHeader.tsx';
import { Button } from '../components/button/Button.tsx';
import { Field, FieldList } from '../components/field/Field.tsx';
import { TextInput } from '../components/field/TextInput.tsx';
import { ChangeEmailState } from './ChangeEmailState.ts';

const useStyles = createUseStyles({
    body: {
        padding: '16px 25px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,

        // Field wraps content in an IonItem with its own --padding-start (16px) plus an
        // inner-padding-start (16px), which would push the field label/input further in
        // than our 25px body padding. Zero them out so the field visually aligns with
        // surrounding paragraphs and buttons.
        '& ion-item': {
            '--padding-start': '0',
            '--inner-padding-start': '0',
            '--padding-end': '0'
        }
    },
    error: {
        color: 'var(--ion-color-danger)'
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 24
    },
    actionsSpread: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 24
    }
});

export interface IChangeEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ChangeEmailModal: FC<IChangeEmailModalProps> = ({ isOpen, onClose }) => {
    const classes = useStyles();
    const state = useInstance(ChangeEmailState);
    useSubscription(() => state);

    useEffect(() => {
        if (isOpen) {
            state.reset();
        }
    }, [isOpen, state]);

    const { step, newEmail, code, inFlight, errorMessage, errorCode } = state.state;

    const handleDismiss = async () => {
        if (step === 'done') {
            await state.completeAndLogout();
        }
        onClose();
    };

    const title = ({
        'enter-email': 'Change Email',
        'enter-code': 'Enter Verification Code',
        'confirm-merge': 'Merge Existing Account?',
        'done': 'Email Updated'
    } as const)[step];

    return (
        <IonModal isOpen={isOpen} onDidDismiss={handleDismiss}>
            <PageHeader title={title} onClose={handleDismiss} />

            <IonContent>
                {step === 'enter-email' && (
                    <div data-id="change-email-step-enter-email" className={classes.body}>
                        <p>This will change the email address you use to sign in to AutoPylot.</p>
                        <p>Enter the new email address you want to use. We&apos;ll send a 6-digit verification code to that address.</p>
                        <FieldList>
                            <Field dataSet={{ type: 'new-email' }} label="New Email" required>
                                <TextInput
                                    value={newEmail}
                                    onChange={state.setNewEmail}
                                    type="email"
                                    inputmode="email"
                                    maxlength={254}
                                    autofocus
                                />
                            </Field>
                        </FieldList>
                        {errorMessage && (
                            <p data-id="change-email-error" className={classes.error}>{errorMessage}</p>
                        )}
                        <div className={classes.actions}>
                            <Button
                                dataType="change-email-send-code"
                                onClick={() => state.requestCode()}
                                disabled={inFlight || !newEmail}
                            >
                                {inFlight ? 'Sending…' : 'Send Code'}
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'enter-code' && (
                    <div data-id="change-email-step-enter-code" className={classes.body}>
                        <p>We sent a 6-digit code to <strong>{newEmail}</strong>. Enter it below to confirm your email change.</p>
                        <FieldList>
                            <Field dataSet={{ type: 'verification-code' }} label="Verification Code" required>
                                <TextInput
                                    value={code}
                                    onChange={state.setCode}
                                    type="number"
                                    inputmode="numeric"
                                    maxlength={6}
                                    pattern="\\d{6}"
                                    autofocus
                                />
                            </Field>
                        </FieldList>
                        {errorMessage && (
                            <p data-id="change-email-error" className={classes.error}>{errorMessage}</p>
                        )}
                        <div>
                            <a
                                href="#"
                                data-id="change-email-resend"
                                onClick={(e) => { e.preventDefault(); state.resendCode(); }}
                            >
                                Send another code
                            </a>
                        </div>
                        <div className={classes.actions}>
                            <Button
                                dataType="change-email-verify"
                                onClick={() => state.submitCode()}
                                disabled={inFlight || code.length !== 6}
                            >
                                {inFlight ? 'Verifying…' : 'Verify'}
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'confirm-merge' && (
                    <div data-id="change-email-step-confirm-merge" className={classes.body}>
                        <p>WARNING: You already have an AutoPylot account with the email <strong>{newEmail}</strong>.</p>
                        <p>If you continue, any missions, checklists, and drones from that account will be merged with this one. This cannot be undone. (Note that merged items may take a few minutes to appear.)</p>
                        <p>If you are signed in to either account on any other devices, please sign out on those devices before continuing.</p>
                        {errorMessage && (
                            <p data-id="change-email-error" className={classes.error}>{errorMessage}</p>
                        )}
                        <div className={classes.actionsSpread}>
                            <Button
                                dataType="change-email-merge-cancel"
                                fill="outline"
                                onClick={() => { state.cancelMerge(); onClose(); }}
                                disabled={inFlight}
                            >
                                Cancel
                            </Button>
                            <Button
                                dataType="change-email-merge-confirm"
                                onClick={() => state.confirmMerge()}
                                disabled={inFlight}
                            >
                                {inFlight ? 'Working…' : 'Continue'}
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'done' && (
                    <div data-id="change-email-step-done" className={classes.body}>
                        <p>Your email has been updated to <strong>{newEmail}</strong>.</p>
                        <p>Please sign in again with your new email.</p>
                        <div className={classes.actions}>
                            <Button
                                dataType="change-email-finish"
                                onClick={() => handleDismiss()}
                            >
                                Sign In Again
                            </Button>
                        </div>
                    </div>
                )}
            </IonContent>
        </IonModal>
    );
};
